import { useEffect, useState } from "react";

/**
 * BreatheFirst — a 3-second breathing orb shown once per day when the user
 * opens the dashboard. Trains the nervous system to associate Lala Land with
 * a moment of calm before any information arrives. Skippable with a tap.
 *
 * Persistence: localStorage key 'lala_breath_last' stores ISO date string.
 * Fires once per calendar day per device.
 */

const STORAGE_KEY = "lala_breath_last";

const shouldShowInitially = () => {
  try {
    const today = new Date().toISOString().slice(0, 10);
    return localStorage.getItem(STORAGE_KEY) !== today;
  } catch (_e) {
    return true;
  }
};

const BreatheFirst = () => {
  // Lazy-init so the effect never races StrictMode's double-invoke.
  const [visible, setVisible] = useState(shouldShowInitially);
  const [phase, setPhase] = useState("in"); // 'in' | 'out' | 'done'

  useEffect(() => {
    if (!visible) return undefined;
    // Persist NOW so even if the user navigates away mid-breath we don't show it again today
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(STORAGE_KEY, today);
    } catch (_e) { /* private browsing */ }

    const t1 = setTimeout(() => setPhase("out"), 2000);
    const t2 = setTimeout(() => setPhase("done"), 4000);
    const t3 = setTimeout(() => setVisible(false), 4700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[70] flex flex-col items-center justify-center bg-background/95 backdrop-blur-xl transition-opacity duration-700 ${
        phase === "done" ? "opacity-0" : "opacity-100"
      }`}
      onClick={() => setVisible(false)}
      role="dialog"
      aria-label="Breathing moment"
      data-testid="breathe-first-overlay"
    >
      <div className="relative flex items-center justify-center">
        {/* Glow ring */}
        <div
          className="absolute rounded-full bg-emerald-400/20 blur-3xl transition-all duration-[2000ms] ease-in-out"
          style={{
            width: phase === "in" ? "360px" : "180px",
            height: phase === "in" ? "360px" : "180px",
          }}
        />
        {/* Orb */}
        <div
          className="rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 shadow-2xl transition-all duration-[2000ms] ease-in-out"
          style={{
            width: phase === "in" ? "220px" : "110px",
            height: phase === "in" ? "220px" : "110px",
            boxShadow: "0 0 80px rgba(52, 211, 153, 0.6), 0 0 160px rgba(52, 211, 153, 0.35)",
          }}
        />
      </div>
      <p className="mt-10 text-lg font-light tracking-[0.3em] uppercase text-emerald-300 animate-fade-in">
        {phase === "in" ? "Inhale" : phase === "out" ? "Exhale" : ""}
      </p>
      <p className="mt-8 text-xs text-muted-foreground/70 tracking-wide">
        tap to skip
      </p>
    </div>
  );
};

export default BreatheFirst;
